import type { ITaxApplyPayload } from '@/models/purchase_order';

/**
 * AS-10746 — the pure logic behind "Apply Tax" on an already-created PO.
 *
 * Kept out of PurchaseOrderDetail.vue (which is already very large) and free of
 * Vue/HTTP so the arithmetic and gating are unit-testable directly.
 *
 * Tax is the one money field that stays editable after confirmation: it does
 * not enter inventory valuation, so re-applying VAT to a received PO cannot
 * corrupt the unit cost already pushed to Shopify. Quantity and unit price do
 * not share that property, which is why this is its own action rather than a
 * relaxation of the draft-only edit gate.
 */

/** Statuses on which the backend accepts PATCH /purchase_orders/:id/items/tax. */
export const TAX_EDITABLE_STATUSES = [
  'draft',
  'pending_approval',
  'confirmed',
  'partial_received',
  'received',
] as const;

export interface TaxApplyLine {
  id?: number | string;
  orderedQuantity?: number | string;
  unitPrice?: number | string;
  extra?: {
    financial?: {
      tax_type_id?: number | null;
      tax_type_name?: string | null;
      tax_rate?: number | string | null;
      tax_amount?: number | string | null;
    };
  };
}

export interface TaxTypeOption {
  id: number;
  name: string;
  rate: number;
  isDefault?: boolean;
}

/** What the dialog is asking for: a named tax type, or removal. */
export type TaxSelection = { taxTypeId: number } | { clear: true };

export function canApplyTax(status?: string | null): boolean {
  if (!status) return false;
  return (TAX_EDITABLE_STATUSES as readonly string[]).includes(status);
}

/**
 * Why the action is unavailable, for the tooltip. Returns null when it IS
 * available — `completed` gets its own message because it has a reopen path.
 */
export function taxLockReason(status?: string | null): string | null {
  if (canApplyTax(status)) return null;
  if (status === 'completed') {
    return 'Re-open this purchase order before changing tax';
  }
  if (status === 'canceled' || status === 'deleted') {
    return 'This purchase order is closed and cannot be changed';
  }
  return 'Tax cannot be changed on this purchase order';
}

const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** The rate currently on a line, as a fraction. */
export function currentTaxRate(line: TaxApplyLine): number {
  return toNumber(line?.extra?.financial?.tax_rate);
}

/**
 * Tax the line would carry under `selection`, recomputed from the line's OWN
 * current quantity and price — never from the stored tax_amount, which goes
 * stale the moment a quantity adjustment lands.
 */
export function previewLineTax(line: TaxApplyLine, selection: TaxSelection, taxTypes: TaxTypeOption[]): number {
  if ('clear' in selection) return 0;
  const taxType = taxTypes.find((t) => t.id === selection.taxTypeId);
  if (!taxType) return 0;
  return toNumber(line.orderedQuantity) * toNumber(line.unitPrice) * toNumber(taxType.rate);
}

/** Total tax across the lines under `selection`. Rounded to 2dp for display. */
export function previewTotalTax(lines: TaxApplyLine[], selection: TaxSelection, taxTypes: TaxTypeOption[]): number {
  const sum = (lines || []).reduce((acc, line) => acc + previewLineTax(line, selection, taxTypes), 0);
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/** Tax the lines carry right now. */
export function currentTotalTax(lines: TaxApplyLine[]): number {
  const sum = (lines || []).reduce(
    (acc, line) => acc + toNumber(line.orderedQuantity) * toNumber(line.unitPrice) * currentTaxRate(line),
    0
  );
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

/**
 * Submit is allowed only when the selection would actually change something.
 * Re-submitting the same tax would succeed on the backend as a 400 "no tax
 * changes to apply"; disabling here turns that into a quiet no-op instead of an
 * error toast.
 */
export function canSubmitTax(
  lines: TaxApplyLine[],
  selection: TaxSelection | null,
  taxTypes: TaxTypeOption[]
): boolean {
  if (!selection) return false;
  if (!lines || lines.length === 0) return false;

  if ('clear' in selection) {
    return lines.some((line) => currentTaxRate(line) !== 0 || line?.extra?.financial?.tax_type_id != null);
  }

  const taxType = taxTypes.find((t) => t.id === selection.taxTypeId);
  if (!taxType) return false;

  return lines.some(
    (line) =>
      currentTaxRate(line) !== toNumber(taxType.rate) ||
      Number(line?.extra?.financial?.tax_type_id) !== taxType.id
  );
}

// The wire contract lives with the model that sends it. Re-exported here so
// callers of buildTaxApplyPayload get the type without a second declaration
// drifting from the one `applyTax` actually posts.
export type { ITaxApplyPayload } from '@/models/purchase_order';

export function buildTaxApplyPayload(
  selection: TaxSelection,
  reason?: string,
  notes?: string,
): ITaxApplyPayload {
  const payload: ITaxApplyPayload = {
    applyToAll: 'clear' in selection ? { clear: true } : { taxTypeId: selection.taxTypeId },
  };
  // Omitted rather than sent empty: the backend stores these verbatim on the
  // history row, and "" reads differently from "not given" in the audit trail.
  if (reason && reason.trim()) payload.reason = reason.trim();
  if (notes && notes.trim()) payload.notes = notes.trim();
  return payload;
}

/**
 * The tax type to preselect: the supplier's configured default, else the
 * merchant-wide one. Mirrors the backend's resolution order so the dialog opens
 * on the same answer a newly created line would have received.
 */
export function defaultTaxSelection(
  taxTypes: TaxTypeOption[],
  supplierDefaultTaxTypeId?: number | string | null
): number | null {
  if (supplierDefaultTaxTypeId != null) {
    // Loose compare: the id arrives from JSON supplier options as a string.
    /* eslint-disable-next-line eqeqeq */
    const supplierDefault = taxTypes.find((t) => t.id == supplierDefaultTaxTypeId);
    if (supplierDefault) return supplierDefault.id;
  }
  const merchantDefault = taxTypes.find((t) => t.isDefault);
  return merchantDefault ? merchantDefault.id : null;
}

/**
 * Pull a human message out of an axios error.
 *
 * `http.ts` configures a `transformResponse` that returns the body untouched,
 * so `error.response.data` is very often the raw JSON STRING. Reading
 * `.message` off it yields undefined and the user sees the generic fallback
 * instead of the backend's actual explanation ("Re-open this purchase order
 * first", "No tax changes to apply").
 */
export function extractApiErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  let body: any = null;
  if (data && typeof data === 'object') {
    body = data;
  } else if (typeof data === 'string') {
    try {
      body = JSON.parse(data);
    } catch {
      // A non-JSON body (an HTML error page, say) is not worth showing raw.
      body = null;
    }
  }
  if (body && typeof body.message === 'string' && body.message) return body.message;
  if (typeof err?.message === 'string' && err.message) return err.message;
  return fallback;
}
